import { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon
} from '@heroicons/react/24/outline';
import { Loading, Button } from '../../components/common';
import { getAffiliates, updateAffiliate } from '../../services/adminService';

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: ClockIcon },
  approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon },
  suspended: { bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircleIcon }
};

const AffiliatesPage = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    commissionType: '',
    commissionValue: '',
    isRecurring: false,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAffiliates();
  }, [statusFilter]);

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 50,
        status: statusFilter || undefined
      };

      const response = await getAffiliates(params);
      setAffiliates(response.data.affiliates);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (affiliate) => {
    setEditForm({
      status: affiliate.status,
      commissionType: affiliate.commissionType,
      commissionValue: affiliate.commissionValue.toString(),
      isRecurring: affiliate.isRecurring,
      notes: affiliate.notes || ''
    });
    setEditingAffiliate(affiliate);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    if (!editingAffiliate) return;

    try {
      setSubmitting(true);
      await updateAffiliate(editingAffiliate._id, {
        ...editForm,
        commissionValue: Number(editForm.commissionValue)
      });
      setEditingAffiliate(null);
      fetchAffiliates();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update affiliate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickApprove = async (affiliate) => {
    try {
      await updateAffiliate(affiliate._id, { status: 'approved' });
      fetchAffiliates();
    } catch (err) {
      setError(err.message || 'Failed to approve affiliate');
    }
  };

  const handleQuickReject = async (affiliate) => {
    if (!window.confirm('Are you sure you want to reject this affiliate?')) {
      return;
    }

    try {
      await updateAffiliate(affiliate._id, { status: 'rejected' });
      fetchAffiliates();
    } catch (err) {
      setError(err.message || 'Failed to reject affiliate');
    }
  };

  if (loading && affiliates.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading size="lg" text="Loading affiliates..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Affiliates</h1>
        <p className="text-gray-500 mt-1">Manage affiliate partners and commissions</p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-sm text-red-700 underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Edit Modal */}
      {editingAffiliate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Edit Affiliate
              </h2>
              <p className="text-sm text-gray-500">
                {editingAffiliate.userId?.email}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={editForm.status}
                  onChange={handleEditChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commission Type
                  </label>
                  <select
                    name="commissionType"
                    value={editForm.commissionType}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="flat">Flat Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commission Value
                  </label>
                  <input
                    type="number"
                    name="commissionValue"
                    value={editForm.commissionValue}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isRecurring"
                  checked={editForm.isRecurring}
                  onChange={handleEditChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Recurring commission on renewals</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Notes
                </label>
                <textarea
                  name="notes"
                  value={editForm.notes}
                  onChange={handleEditChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Internal notes about this affiliate..."
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => setEditingAffiliate(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  loading={submitting}
                  disabled={submitting}
                  className="flex-1"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Affiliates List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left text-sm text-gray-500">
                <th className="px-6 py-3 font-medium">Affiliate</th>
                <th className="px-6 py-3 font-medium">Code</th>
                <th className="px-6 py-3 font-medium">Commission</th>
                <th className="px-6 py-3 font-medium">Stats</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {affiliates.map((affiliate) => {
                const statusStyle = STATUS_STYLES[affiliate.status] || STATUS_STYLES.pending;
                const StatusIcon = statusStyle.icon;

                return (
                  <tr key={affiliate._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {affiliate.companyName || affiliate.userId?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-500">{affiliate.userId?.email}</p>
                        {affiliate.website && (
                          <a
                            href={affiliate.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:underline"
                          >
                            {affiliate.website}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                        {affiliate.couponCode || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {affiliate.commissionType === 'percentage'
                            ? `${affiliate.commissionValue}%`
                            : `₹${affiliate.commissionValue}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {affiliate.isRecurring ? 'Recurring' : 'One-time'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-gray-700">
                          {affiliate.totalConversions || 0} conversions
                        </p>
                        <p className="text-sm font-medium text-green-600 flex items-center gap-1">
                          <CurrencyRupeeIcon className="h-3.5 w-3.5" />
                          {(affiliate.totalEarnings || 0).toLocaleString()} earned
                        </p>
                        {affiliate.pendingPayout > 0 && (
                          <p className="text-xs text-gray-500">
                            ₹{affiliate.pendingPayout.toLocaleString()} pending
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {affiliate.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleQuickApprove(affiliate)}
                              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleQuickReject(affiliate)}
                              className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEdit(affiliate)}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {affiliates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No affiliates found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatesPage;
